import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import React from 'react';

import { Controller, useForm } from 'react-hook-form';
import { AccessPointData, PermissionData, RequestPermission, RequestPermissionPost } from '../../api/types';
import { Locale } from '../../translations/translations';
import { DatePickerWrapper } from '../DatePicker/DatePicker';
import { RequestAccessFormExperiments } from './RequestAccessFormExperiments';

interface RequestAccessFormProps {
    accessInformation: AccessPointData;
    currentLanguage: Locale;
    permissionInfo: PermissionData;
    requestAccess: (arg: RequestPermissionPost) => void;
    translations: Record<string, string>;
}

interface RequestAccessFormData {
    startDate: string;
    endDate: string;
    selectedExperiment: string;
    reason: string;
    alert: boolean;
}

export const RequestAccessForm: React.FC<RequestAccessFormProps> = ({
    accessInformation,
    currentLanguage,
    permissionInfo,
    requestAccess,
    translations,
}) => {
    const {
        control,
        handleSubmit,
        setValue,
        formState: { isSubmitting },
    } = useForm<RequestAccessFormData>({
        defaultValues: {
            startDate: permissionInfo.requestStartDate,
            endDate: permissionInfo.requestEndDate,
            selectedExperiment: '',
            reason: '',
            alert: false,
        },
    });

    const onSubmit = (data: RequestAccessFormData) => {
        const requestData: RequestPermission = {
            personID: accessInformation.personID,
            permissionID: permissionInfo.permissionID,
            requestStartDate: data.startDate,
            requestEndDate: data.endDate,
            experiment: data.selectedExperiment,
            reason: data.reason,
            alert: data.alert,
        };

        requestAccess({ data: requestData, language: currentLanguage });
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Box mt={2}>
                <Controller
                    name="startDate"
                    control={control}
                    render={({ field }) => (
                        <DatePickerWrapper
                            label={translations.ACRQ_START_DATE_LABEL}
                            value={field.value}
                            onChange={field.onChange}
                        />
                    )}
                />
            </Box>

            <Box mt={2}>
                <Controller
                    name="endDate"
                    control={control}
                    render={({ field }) => (
                        <DatePickerWrapper
                            label={translations.ACRQ_END_DATE_LABEL}
                            value={field.value}
                            onChange={field.onChange}
                        />
                    )}
                />
            </Box>

            {permissionInfo.requestExperiments?.length > 0 && (
                <RequestAccessFormExperiments
                    experiments={permissionInfo.requestExperiments}
                    onChange={(value) => setValue('selectedExperiment', value)}
                    translations={translations}
                />
            )}

            <Box my={2}>
                <Controller
                    name="reason"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            data-testid="reason"
                            multiline
                            fullWidth
                            label={translations.ACRQ_REASON_LABEL}
                            variant="standard"
                            rows={3}
                            value={field.value}
                            onChange={field.onChange}
                        />
                    )}
                />
            </Box>

            <FormControlLabel
                control={
                    <Controller
                        name="alert"
                        control={control}
                        render={({ field }) => (
                            <Checkbox
                                checked={field.value}
                                onChange={(e) => field.onChange(e.target.checked)}
                                color="primary"
                            />
                        )}
                    />
                }
                label={translations.ACRQ_ALERT_LABEL}
            />

            <Box mt={4}>
                <Button
                    data-testid="submit"
                    fullWidth
                    disabled={isSubmitting}
                    variant="contained"
                    size="large"
                    color="primary"
                    type="submit"
                >
                    {translations.GLOBAL_SUBMIT_BUTTON}
                </Button>
            </Box>
        </form>
    );
};
